/**
 * Utility functions for saved filters functionality
 * These functions handle the API interactions for saving and applying filters with overrides
 */
import { authenticatedFetch } from './apiUtils';

/**
 * Save a filter configuration with overrides
 * @param {Object} filterData - Filter data to save
 * @param {string} filterData.name - Name of the filter
 * @param {string} filterData.description - Optional description
 * @param {string} filterData.filterId - ID of the filter whose results to save
 * @param {Object} filterData.overrides - Optional user overrides for filter decisions
 * @param {boolean} filterData.isFavorite - Whether the filter is marked as favorite
 * @returns {Promise<Object>} - Saved filter data
 */
export const saveSavedFilter = async (filterData) => {
  try {
    const { name, description, filterId, overrides, isFavorite } = filterData;
    
    if (!name || !filterId) {
      throw new Error('Filter name and ID are required');
    }
    
    // Get the filter configuration from the server
    const response = await authenticatedFetch(`/api/results/${filterId}`);
    if (!response.ok) {
      throw new Error('Could not retrieve filter configuration');
    }
    
    const filterResults = await response.json();
    console.log('Filter results retrieved for saving:', filterResults);
    
    // Prepare filter data for saving with a standardized structure
    // Extract column-specific instructions into columnConfig object
    const columnConfig = {};
    const selectedColumns = [];
    
    // Process each column config from the filter results
    // First, check for top-level column configuration objects
    Object.keys(filterResults).forEach(key => {
      // Check if the key is a column with instructions (not a standard filter property)
      if (typeof filterResults[key] === 'object' && 
          !Array.isArray(filterResults[key]) && 
          key !== 'selectedColumns' && 
          key !== 'overrides' && 
          key !== 'columnConfig' && 
          !['model', 'reasoningText', 'temperature', 'chunkSize'].includes(key)) {
        
        // Add to columnConfig with standard structure
        if (filterResults[key].instructions !== undefined) {
          // Already has proper structure
          columnConfig[key] = filterResults[key];
        } else {
          // Convert to standard structure with instructions property
          columnConfig[key] = { 
            instructions: JSON.stringify(filterResults[key]),
            ...filterResults[key] // Keep other properties too
          };
        }
        
        console.log(`Added column ${key} to columnConfig with structure:`, columnConfig[key]);
        
        // Add to selectedColumns if not already there
        if (!selectedColumns.includes(key)) {
          selectedColumns.push(key);
          console.log(`Added column ${key} to selectedColumns from direct configuration`); 
        }
      }
    });
    
    // Check for a nested columnConfig object and merge it
    if (filterResults.columnConfig && typeof filterResults.columnConfig === 'object') {
      console.log('Found nested columnConfig, merging with extracted config');
      Object.keys(filterResults.columnConfig).forEach(key => {
        // Use standardized structure with instructions property
        if (filterResults.columnConfig[key].instructions !== undefined) {
          // Already has proper structure
          columnConfig[key] = filterResults.columnConfig[key];
        } else {
          // Convert to standard structure with instructions property
          columnConfig[key] = { 
            instructions: '',
            ...filterResults.columnConfig[key] // Keep all original properties 
          };
        }
        
        console.log(`Added/updated column ${key} from nested columnConfig with structure:`, columnConfig[key]);
        
        // Also add to selectedColumns if not already there
        if (!selectedColumns.includes(key)) {
          selectedColumns.push(key);
          console.log(`Added column ${key} from nested columnConfig to selectedColumns`);
        }
      });
    }
    
    // If we still have selectedColumns in the results, use those too
    if (filterResults.selectedColumns && Array.isArray(filterResults.selectedColumns)) {
      console.log('Found explicit selectedColumns array, merging with extracted columns');
      filterResults.selectedColumns.forEach(col => {
        if (!selectedColumns.includes(col)) {
          selectedColumns.push(col);
          console.log(`Added explicit column ${col} to selectedColumns`);
        }
      });
    }
    
    console.log('Final standardized columnConfig:', columnConfig);
    console.log('Final standardized selectedColumns:', selectedColumns);
    
    // Ensure we have at least empty objects/arrays for required fields
    if (Object.keys(columnConfig).length === 0) {
      console.warn('No column configuration found in filter results, creating empty structure');
    }
    
    if (selectedColumns.length === 0) {
      console.warn('No selected columns found in filter results');
    }
    
    // Try to get the reasoning text from various sources
    // First check the filterResults, which may not have it
    let reasoningText = filterResults.reasoningText || '';
    
    // If not found, check localStorage for savedFilterConfig which might have it
    if (!reasoningText) {
      try {
        const savedFilterConfig = localStorage.getItem('savedFilterConfig');
        if (savedFilterConfig) {
          const parsedConfig = JSON.parse(savedFilterConfig);
          if (parsedConfig.reasoningText) {
            reasoningText = parsedConfig.reasoningText;
            console.log('Retrieved reasoning text from savedFilterConfig:', reasoningText.substring(0, 50) + '...');
          }
        }
      } catch (error) {
        console.warn('Error retrieving reasoning text from localStorage:', error);
      }
    }
    
    // Create standardized save data structure
    const saveData = {
      name,
      description: description || '',
      filterConfig: {
        selectedColumns: selectedColumns,
        reasoningText: reasoningText, // Use our extracted reasoning text
        model: filterResults.model || 'o4-mini-high',
        chunkSize: filterResults.chunkSize || 300,
        columnConfig: columnConfig  // Include the properly structured columnConfig
      },
      overrides: overrides || {}, 
      isFavorite: isFavorite || false
    };
    
    console.log('Using reasoning text in saved filter:', reasoningText ? reasoningText.substring(0, 50) + '...' : 'None');
    
    console.log('Saving filter with standardized structure:', saveData);
    
    // Save to server
    const saveResponse = await authenticatedFetch('/api/saved-filters', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(saveData)
    });
    
    if (!saveResponse.ok) {
      const errorData = await saveResponse.json();
      throw new Error(errorData.error || 'Failed to save filter');
    }
    
    return await saveResponse.json();
  } catch (error) {
    console.error('Save filter error:', error);
    throw error;
  }
};

/**
 * Get all saved filters for the current user
 * @returns {Promise<Array>} - List of saved filters
 */
export const getSavedFilters = async () => {
  try {
    const response = await authenticatedFetch('/api/saved-filters');
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch saved filters');
    }
    
    const result = await response.json();
    return result.filters || [];
  } catch (error) {
    console.error('Get saved filters error:', error);
    throw error;
  }
};

/**
 * Get a saved filter by ID
 * @param {string} filterId - ID of the saved filter to retrieve
 * @returns {Promise<Object>} - Saved filter details
 */
export const getSavedFilterById = async (filterId) => {
  try {
    if (!filterId) {
      throw new Error('Filter ID is required');
    }
    
    console.log(`Retrieving saved filter with ID: ${filterId}`);
    const response = await authenticatedFetch(`/api/saved-filters/${filterId}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch saved filter');
    }
    
    const result = await response.json();
    console.log('Retrieved filter details from API:', result);
    return result;
  } catch (error) {
    console.error(`Get saved filter by ID error for ${filterId}:`, error);
    throw error;
  }
};

/**
 * Apply a saved filter to a dataset
 * @param {string} fileId - ID of the file to apply the filter to
 * @param {string} savedFilterId - ID of the saved filter to apply
 * @returns {Promise<Object>} - Result with the new filter ID
 */
export const applySavedFilter = async (fileId, savedFilterId) => {
  try {
    console.log('Applying saved filter with params:', { fileId, savedFilterId });
    
    if (!fileId || !savedFilterId) {
      throw new Error('File ID and saved filter ID are required');
    }
    
    // First, let's get the saved filter details to see what we're applying
    const getFilterResponse = await authenticatedFetch(`/api/saved-filters/${savedFilterId}`);
    if (!getFilterResponse.ok) {
      throw new Error('Could not retrieve saved filter details');
    }
    
    const savedFilter = await getFilterResponse.json();
    console.log('Retrieved saved filter details:', savedFilter);
    
    // Now apply the saved filter with complete configuration information
    // Check if overrides exist and log detailed information
    const hasOverrides = savedFilter.filter.overrides && 
                         Object.keys(savedFilter.filter.overrides).length > 0;
    
    if (hasOverrides) {
      console.log('OVERRIDES-DEBUG: Found overrides in saved filter:', savedFilter.filter.overrides);
      console.log('OVERRIDES-DEBUG: Overrides count:', Object.keys(savedFilter.filter.overrides).length);
      Object.entries(savedFilter.filter.overrides).forEach(([column, values]) => {
        console.log(`OVERRIDES-DEBUG: Column ${column} has ${Object.keys(values).length} overridden values`);
      });
      
      // Check if filter has the standardized columnConfig structure
      if (savedFilter.filter.filter_config && savedFilter.filter.filter_config.columnConfig) {
        console.log('STRUCTURE-DEBUG: Filter has standardized columnConfig structure');
        console.log('STRUCTURE-DEBUG: columnConfig keys:', Object.keys(savedFilter.filter.filter_config.columnConfig));
      } else {
        console.log('STRUCTURE-DEBUG: Filter does NOT have standardized columnConfig structure');
        console.log('STRUCTURE-DEBUG: filter_config keys:', Object.keys(savedFilter.filter.filter_config || {}));
      }
    } else {
      console.log('OVERRIDES-DEBUG: No overrides found in saved filter');
    }
    
    const payload = { 
      fileId, 
      savedFilterId,
      // Include complete filter configuration for logging purposes
      filterConfig: savedFilter.filter.filter_config,
      overrides: savedFilter.filter.overrides
    };
    
    console.log('Sending apply saved filter payload:', payload);
    
    const response = await authenticatedFetch('/api/apply-saved-filter', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to apply saved filter');
    }
    
    const result = await response.json();
    console.log('Apply saved filter response:', result);
    return result;
  } catch (error) {
    console.error('Apply saved filter error:', error);
    throw error;
  }
};

/**
 * Toggle a filter's favorite status
 * @param {string} filterId - ID of the saved filter to toggle favorite status
 * @returns {Promise<Object>} - Updated filter data
 */
export const toggleFavorite = async (filterId) => {
  try {
    if (!filterId) {
      throw new Error('Filter ID is required');
    }
    
    console.log(`Toggling favorite status for filter ${filterId}`);
    const response = await authenticatedFetch(`/api/saved-filters/${filterId}/toggle-favorite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to toggle favorite status');
    }
    
    const result = await response.json();
    console.log('Toggle favorite response:', result);
    return result;
  } catch (error) {
    console.error(`Toggle favorite error for ${filterId}:`, error);
    throw error;
  }
};

/**
 * Delete a saved filter
 * @param {string} filterId - ID of the saved filter to delete
 * @returns {Promise<Object>} - Result of deletion
 */
export const deleteSavedFilter = async (filterId) => {
  try {
    if (!filterId) {
      throw new Error('Filter ID is required');
    }
    
    const response = await authenticatedFetch(`/api/saved-filters/${filterId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete filter');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Delete filter error:', error);
    throw error;
  }
};
